import { useCallback, useEffect, useState } from "react";
import {
  downloadPricingChangeReportExport,
  fetchPricingChangeReport,
  fetchPricingChangeReportOptions,
  type PricingChangeReport,
  type PricingChangeReportOptions,
  type PricingChangeReportParams,
  type PricingSortColumn,
  type SortDirection,
} from "@/lib/adminPricingReportApi";
import { getApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useApp } from "@/store/useApp";
import { hasPermission } from "@/lib/types";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Download, Eraser, Loader2, Search, Tag } from "lucide-react";

const ALL = "__all__";
const PAGE_SIZE = 50;

function fmtMoney(value: number) {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function productAndCut(productName: string, cutName: string | null) {
  return cutName && cutName.trim() ? `${productName} ${cutName.trim()}` : productName;
}

// Coluna %: seta indica a direção (verde ↑ / vermelho ↓), sem sinal no número; zero sem seta; null → —.
function PercentCell({ value }: { value: number | null }) {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  const magnitude = `${Math.abs(value).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  if (value > 0) {
    return (
      <span className="inline-flex items-center justify-end gap-1 text-emerald-600">
        <ArrowUp className="h-3.5 w-3.5" />{magnitude}
      </span>
    );
  }
  if (value < 0) {
    return (
      <span className="inline-flex items-center justify-end gap-1 text-red-600">
        <ArrowDown className="h-3.5 w-3.5" />{magnitude}
      </span>
    );
  }
  return <span className="text-muted-foreground">{magnitude}</span>;
}

function triggerDownload(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Direção do 1º clique por coluna: texto sobe (A→Z); data/valor descem (mais recente / maior primeiro).
const FIRST_DIR: Record<PricingSortColumn, SortDirection> = {
  changedAt: "desc",
  productName: "asc",
  tableName: "asc",
  actorEmail: "asc",
  oldPrice: "desc",
  newPrice: "desc",
};

export default function AdminPricingReport() {
  const canExport = hasPermission(useApp((s) => s.account), "RELATORIOS.PRECIFICACAO.EXPORTAR");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [productName, setProductName] = useState("");
  const [productCode, setProductCode] = useState("");
  const [cutName, setCutName] = useState("");
  const [tableName, setTableName] = useState<string>(ALL);
  const [actorEmail, setActorEmail] = useState<string>(ALL);

  const [sort, setSort] = useState<PricingSortColumn>("changedAt");
  const [direction, setDirection] = useState<SortDirection>("desc");
  const [page, setPage] = useState(0);

  const [report, setReport] = useState<PricingChangeReport | null>(null);
  const [options, setOptions] = useState<PricingChangeReportOptions>({ tables: [], users: [] });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const currentFilters = useCallback((): PricingChangeReportParams => {
    const codeNum = productCode.trim() ? Number(productCode.trim()) : undefined;
    return {
      from: from || undefined,
      to: to || undefined,
      productName: productName.trim() || undefined,
      productCode: Number.isFinite(codeNum) ? codeNum : undefined,
      cutName: cutName.trim() || undefined,
      tableName: tableName !== ALL ? tableName : undefined,
      actorEmail: actorEmail !== ALL ? actorEmail : undefined,
    };
  }, [from, to, productName, productCode, cutName, tableName, actorEmail]);

  const fetchReport = useCallback(async (params: PricingChangeReportParams) => {
    setPage(params.page ?? 0);
    if (params.sort) setSort(params.sort);
    if (params.direction) setDirection(params.direction);
    setLoading(true);
    try {
      const data = await fetchPricingChangeReport({ ...params, size: PAGE_SIZE });
      setReport(data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao carregar relatório"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPricingChangeReportOptions()
      .then(setOptions)
      .catch(err => toast.error(getApiErrorMessage(err, "Erro ao carregar filtros")));
    void fetchReport({ page: 0, sort: "changedAt", direction: "desc" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = () =>
    void fetchReport({ ...currentFilters(), page: 0, sort, direction });

  const clearFilters = () => {
    setFrom("");
    setTo("");
    setProductName("");
    setProductCode("");
    setCutName("");
    setTableName(ALL);
    setActorEmail(ALL);
    void fetchReport({ page: 0, sort: "changedAt", direction: "desc" });
  };

  const onSort = (col: PricingSortColumn) => {
    const nextDir: SortDirection = col === sort ? (direction === "asc" ? "desc" : "asc") : FIRST_DIR[col];
    void fetchReport({ ...currentFilters(), page: 0, sort: col, direction: nextDir });
  };

  const goToPage = (nextPage: number) =>
    void fetchReport({ ...currentFilters(), page: nextPage, sort, direction });

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const { filename, blob } = await downloadPricingChangeReportExport({ ...currentFilters(), sort, direction });
      triggerDownload(filename, blob);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível gerar o arquivo"));
    } finally {
      setExporting(false);
    }
  };

  const sortHead = (col: PricingSortColumn, label: string, alignRight = false) => (
    <TableHead className={alignRight ? "text-right" : undefined}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 select-none hover:text-foreground ${alignRight ? "justify-end" : ""}`}
      >
        {label}
        {sort === col && (direction === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </TableHead>
  );

  const rows = report?.content ?? [];
  const totalElements = report?.totalElements ?? 0;
  const totalPages = report?.totalPages ?? 0;

  return (
    <div className="flex flex-col gap-6">
      {/* Header + filtros */}
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Precificação</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Histórico de alterações de preço: produto, corte, tabela, valor anterior e novo, data/hora e
              usuário responsável.
            </p>
          </div>
          <Tag className="h-8 w-8 shrink-0 opacity-70" />
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-primary-foreground/70">De</span>
            <DatePicker
              value={from}
              toDate={to || undefined}
              onChange={next => { setFrom(next); if (next && to && next > to) setTo(next); }}
              className="h-8 w-40 bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-primary-foreground/70">Até</span>
            <DatePicker
              value={to}
              fromDate={from || undefined}
              onChange={next => { setTo(next); if (next && from && next < from) setFrom(next); }}
              className="h-8 w-40 bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-primary-foreground/70">Produto</span>
            <Input
              value={productName}
              onChange={e => setProductName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") search(); }}
              placeholder="Nome do produto"
              className="h-8 w-48 bg-white/10 border-white/20 text-primary-foreground placeholder:text-primary-foreground/50 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-primary-foreground/70">Cód.</span>
            <Input
              value={productCode}
              onChange={e => setProductCode(e.target.value.replace(/\D/g, ""))}
              onKeyDown={e => { if (e.key === "Enter") search(); }}
              placeholder="Código"
              inputMode="numeric"
              className="h-8 w-28 bg-white/10 border-white/20 text-primary-foreground placeholder:text-primary-foreground/50 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-primary-foreground/70">Corte</span>
            <Input
              value={cutName}
              onChange={e => setCutName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") search(); }}
              placeholder="Corte"
              className="h-8 w-40 bg-white/10 border-white/20 text-primary-foreground placeholder:text-primary-foreground/50 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-primary-foreground/70">Tabela</span>
            <Select value={tableName} onValueChange={setTableName}>
              <SelectTrigger className="h-8 w-48 bg-white/10 border-white/20 text-primary-foreground text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas as tabelas</SelectItem>
                {options.tables.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-primary-foreground/70">Usuário</span>
            <Select value={actorEmail} onValueChange={setActorEmail}>
              <SelectTrigger className="h-8 w-56 bg-white/10 border-white/20 text-primary-foreground text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos os usuários</SelectItem>
                {options.users.map(u => (
                  <SelectItem key={u} value={u}>{u}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="secondary" className="h-8 gap-1.5" onClick={search} disabled={loading}>
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
            Pesquisar
          </Button>
          <Button
            variant="ghost"
            className="h-8 gap-1.5 text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
            onClick={clearFilters}
            disabled={loading}
          >
            <Eraser className="h-3.5 w-3.5" /> Limpar
          </Button>
          {canExport && (
            <Button
              variant="secondary"
              className="h-8 gap-1.5"
              onClick={() => void exportXlsx()}
              disabled={exporting || totalElements === 0}
            >
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              XLSX
            </Button>
          )}
        </div>
      </div>

      {/* Resultados */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground">
          <Tag className="h-10 w-10 opacity-30" />
          <p className="text-sm">Nenhuma alteração de preço encontrada para os filtros selecionados</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <span className="text-sm font-medium text-muted-foreground">Alterações de preço</span>
            <span className="text-xs text-muted-foreground">{totalElements} registro(s)</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cód.</TableHead>
                {sortHead("productName", "Produto + Corte")}
                {sortHead("tableName", "Tabela")}
                {sortHead("oldPrice", "Vlr. anterior", true)}
                {sortHead("newPrice", "Valor novo", true)}
                <TableHead className="text-right">%</TableHead>
                {sortHead("changedAt", "Data de alteração")}
                <TableHead>Hora</TableHead>
                {sortHead("actorEmail", "Usuário")}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, idx) => (
                <TableRow key={`${r.changedAt}-${r.productCode ?? r.productName}-${idx}`}>
                  <TableCell className="tabular-nums text-muted-foreground">{r.productCode ?? "—"}</TableCell>
                  <TableCell className="font-medium">{productAndCut(r.productName, r.cutName)}</TableCell>
                  <TableCell className="text-muted-foreground">{r.tableName}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{fmtMoney(r.oldPrice)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{fmtMoney(r.newPrice)}</TableCell>
                  <TableCell className="text-right tabular-nums"><PercentCell value={r.changePercent} /></TableCell>
                  <TableCell className="tabular-nums">{r.date}</TableCell>
                  <TableCell className="tabular-nums text-muted-foreground">{r.time}</TableCell>
                  <TableCell className="text-muted-foreground">{r.actorEmail}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Paginação */}
          <div className="flex items-center justify-between border-t px-4 py-3">
            <span className="text-xs text-muted-foreground">
              Página {page + 1} de {Math.max(totalPages, 1)}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 0 || loading}
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages - 1 || loading}
              >
                Próxima <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
